<template>
  <div :class="domainClasses.container">
    <h4 v-if="domain === 'ADMIN'" class="accent">
      {{ $t('label_interpretation_history') }}
    </h4>

    <div class="container-table">
      <HintHorizontal v-if="chatDetailList.length > 0" />

      <div class="top-wrap">
        <div class="web-tab-wrap">
          <button
            type="button"
            v-for="filter in CHAT_TYPE_DETAIL_FILTERS"
            :key="filter.value"
            :class="{ tab: true, on: filter.value === searchParams.chatType }"
            @click="handleFilterClick(filter)"
          >
            {{ t(filter.label) }}
          </button>
        </div>

        <InputSearch v-model="searchQuery" :placeholder="$t('alert_enter_search_keywords')" @search="handleSearch" />
      </div>

      <TableDynamic
        :columns="tableColumns"
        :rows="chatDetailList"
        :showTagFilter="false"
        :show-more-button="pagination.hasMore"
        :loading="isLoading"
        tableClass="no-hover"
        @showMoreClick="showDetail"
        @more-click="showMore"
        :moreButtonText="$t('label_load_more_interpretation_history')"
      />

      <ModalDynamicTableDetail v-model="isModalOpen" :selected-row="selectedRow" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { cloneDeep } from 'lodash';

import InputSearch from '@/components/shared/base/InputSearch.vue';
import ModalDynamicTableDetail from '@/components/shared/base/ModalDynamicTableDetail.vue';
import TableDynamic from '@/components/shared/base/TableDynamic.vue';
import HintHorizontal from '@/components/shared/composite/HintHorizontal.vue';
import { useChatDetailList } from '@/composables/domains/mobile/useChatDetailList';
import { TypeDomain } from '@/types/common';
import { ChatDetailItem } from '@/types/components';
import { CHAT_TYPE_DETAIL_FILTERS } from '@/utils/constants/common';

const props = withDefaults(
  defineProps<{
    domain?: TypeDomain;
  }>(),
  {
    domain: 'ADMIN',
  }
);
const { t } = useI18n();

const { isLoading, chatDetailList, searchParams, pagination, setFilter, fetchChatDetailList } = useChatDetailList();

const searchQuery = ref<string>('');
const isModalOpen = ref<boolean>(false);
const selectedRow = ref<ChatDetailItem>({});

const domainClasses = computed<Record<string, string>>(() => {
  return (
    {
      ADMIN: { container: 'container-callhistory' },
      SELF_SERVICE: { container: 'selfservice-container-callhistory' },
    }[props.domain] || { container: 'container-callhistory' }
  );
});

const tableColumns = computed(() => [
  { label: ' ', type: 'showMore', class: 'width3rem' },
  {
    label: t('label_interpretation_name'),
    key: 'chatName',
    type: 'text',
    class: 'width15',
  },
  {
    label: t('label_interpretation_start_time'),
    key: 'startedDisplayDate',
    type: 'text',
    class: 'width20',
  },
  {
    label: t('label_interpretation_end_time'),
    key: 'endedDisplayDate',
    type: 'text',
    class: 'width20',
    format: (text) => text || t('label_interpreting'),
  },
  {
    label: t('label_attendees'),
    key: 'attendees',
    type: 'text',
    class: 'width30',
    format: (attendees) => attendees.join(', '),
  },
  {
    label: t('label_description'),
    key: 'description',
    type: 'text',
    class: 'width15',
  },
]);

const showDetail = (row) => {
  selectedRow.value = cloneDeep({
    chatName: row.chatName,
    receiver: row.attendees.join(', '),
    usagePeriod: `${row.startedDisplayDate} - ${row.endedDisplayDate ?? ''}`,
    partnerGroupName: row.description,
  });

  isModalOpen.value = true;
};

const showMore = () => {
  fetchChatDetailList(pagination.value.nextOffset);
};

const handleSearch = () => {
  setFilter({ keyword: searchQuery.value.trim() });
};

const handleFilterClick = (filter) => {
  setFilter({ chatType: filter.value });
};

onMounted(async () => {
  setFilter({ chatType: 'ALL' });
});
</script>

<style lang="scss" scoped>
.selfservice-container-callhistory {
  width: 100%;
  height: unset;
  min-width: 0;
  gap: 1.5rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;
}

.container-callhistory {
  width: 100%;
  height: unset;
  max-width: 75rem;
  min-width: 0;
  gap: 1.5rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.container-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  .top-wrap {
    width: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    .search-container {
      max-width: 400px;
      :deep(.search-box) {
        width: 100%;
        min-width: 400px;
      }
    }
    @include respond-to(mobile-landscape) {
      .search-container {
        :deep(.search-box) {
          width: 100%;
          min-width: auto;
        }
      }
    }
  }
}

::v-deep(.search-container) {
  margin-bottom: unset;
}

::v-deep(.dynamic-table) {
  min-width: 60rem;
}

.dynamic-table tr:hover {
  background-color: unset;
}

@include respond-to(mobile-landscape) {
  .container-table {
    .top-wrap {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
      .search-container {
        max-width: 100%;
      }
    }
  }
}
</style>
